/**
 * Where a connection on a plane runs: from the middle of the side of one item
 * that faces the other, as a soft S. Left or right sides draw a horizontal S,
 * top or bottom a vertical one. Canvas units throughout.
 */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export type Side = 'left' | 'right' | 'top' | 'bottom';

export const SIDES: readonly Side[] = ['top', 'right', 'bottom', 'left'];

export interface Curve {
  d: string;
  /** The target end, where the dot sits. */
  end: Point;
  /** Halfway along the curve, where its × sits. */
  mid: Point;
}

function centre(b: Box): Point {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}

/**
 * The sides of [a] and [b] that face each other: along the axis where the
 * gap between them is wider, so two items side by side join at their flanks
 * and two stacked ones at top and bottom.
 */
export function facingSides(a: Box, b: Box): [Side, Side] {
  const ca = centre(a);
  const cb = centre(b);
  const gapX = Math.abs(cb.x - ca.x) - (a.w + b.w) / 2;
  const gapY = Math.abs(cb.y - ca.y) - (a.h + b.h) / 2;
  if (gapX >= gapY) return cb.x >= ca.x ? ['right', 'left'] : ['left', 'right'];
  return cb.y >= ca.y ? ['bottom', 'top'] : ['top', 'bottom'];
}

export function sidePoint(b: Box, side: Side): Point {
  switch (side) {
    case 'left':
      return { x: b.x, y: b.y + b.h / 2 };
    case 'right':
      return { x: b.x + b.w, y: b.y + b.h / 2 };
    case 'top':
      return { x: b.x + b.w / 2, y: b.y };
    case 'bottom':
      return { x: b.x + b.w / 2, y: b.y + b.h };
  }
}

function isHorizontal(side: Side): boolean {
  return side === 'left' || side === 'right';
}

/** An S from [p1] to [p2], bending along the axis [side] leaves on. */
export function sCurve(p1: Point, p2: Point, side: Side): Curve {
  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const d = isHorizontal(side)
    ? `M${p1.x} ${p1.y} C${mid.x} ${p1.y}, ${mid.x} ${p2.y}, ${p2.x} ${p2.y}`
    : `M${p1.x} ${p1.y} C${p1.x} ${mid.y}, ${p2.x} ${mid.y}, ${p2.x} ${p2.y}`;
  // Both control points sit on the midline, so the curve passes through the
  // midpoint of its ends.
  return { d, end: p2, mid };
}

export function curveBetween(a: Box, b: Box): Curve {
  const [from, to] = facingSides(a, b);
  return sCurve(sidePoint(a, from), sidePoint(b, to), from);
}

/** The item under [p], leaving out [except]; the last drawn wins, as on screen. */
export function boxAt<K>(boxes: ReadonlyMap<K, Box>, p: Point, except?: K): K | null {
  let hit: K | null = null;
  for (const [key, b] of boxes) {
    if (key === except) continue;
    if (p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h) hit = key;
  }
  return hit;
}
